import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { AuthService } from '../services/auth'
import { UserService, type User } from '../services/users'
import { StudentSummaryService } from '../services/studentSummaries'
import { VideoService, type WatchHistoryItem } from '../services/videos'
import { ConversationService, type ConversationWithDetails } from '../services/conversations'
import type { StudentDailySummary } from '../types/studentSummary'
import { supabase } from '../lib/supabase'
import { PortfolioService, PORTFOLIO_FIELDS, type StudentPortfolio } from '../services/portfolio'
import { compareStudentsForRoster } from '../utils/gradeClass'

const TeacherStudentDetailPage = () => {
  const { studentId } = useParams<{ studentId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const schoolId = searchParams.get('schoolId')
  const filterGrade = searchParams.get('grade')
  const filterClass = searchParams.get('class')
  const filterGroupId = searchParams.get('groupId')

  const [student, setStudent] = useState<User | null>(null)
  const [summaries, setSummaries] = useState<StudentDailySummary[]>([])
  const [videoHistory, setVideoHistory] = useState<WatchHistoryItem[]>([])
  const [chatHistory, setChatHistory] = useState<ConversationWithDetails[]>([])
  const [videoCount, setVideoCount] = useState(0)
  const [chatCount, setChatCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // Navigation state for prev/next student
  const [prevStudentId, setPrevStudentId] = useState<string | null>(null)
  const [nextStudentId, setNextStudentId] = useState<string | null>(null)
  const [portfolio, setPortfolio] = useState<StudentPortfolio | null>(null)

  // Build URL with preserved filter params
  const buildLinkWithParams = (path: string) => {
    const params = new URLSearchParams()
    if (schoolId) params.set('schoolId', schoolId)
    if (filterGrade) params.set('grade', filterGrade)
    if (filterClass) params.set('class', filterClass)
    if (filterGroupId) params.set('groupId', filterGroupId)
    const queryString = params.toString()
    return `${path}${queryString ? `?${queryString}` : ''}`
  }

  const backLink = buildLinkWithParams('/teacher')
  const prevStudentLink = prevStudentId ? buildLinkWithParams(`/teacher/students/${prevStudentId}`) : null
  const nextStudentLink = nextStudentId ? buildLinkWithParams(`/teacher/students/${nextStudentId}`) : null

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0)

    const loadStudentDetails = async () => {
      try {
        setIsLoading(true)
        setError('')

        // Get current user (teacher or admin)
        const currentUser = await AuthService.getCurrentUser()
        if (!currentUser || !['teacher', 'admin', 'super_admin'].includes(currentUser.role)) {
          navigate('/mypage')
          return
        }

        if (!studentId) {
          setError('生徒IDが指定されていません')
          return
        }

        // Get student details
        const studentData = await UserService.getUser(studentId)
        if (!studentData) {
          setError('生徒が見つかりません')
          return
        }

        // Security check: teachers only see their own school. admin and super_admin can view
        // all, and their list comes from the school picked in the header, not their school_id.
        if (currentUser.role === 'teacher' && currentUser.school_id !== studentData.school_id) {
          setError('この生徒の情報を閲覧する権限がありません')
          return
        }

        setStudent(studentData)

        // Load data in parallel
        const [summaryData, videoData, chatData, activityCounts, studentList, portfolioData] = await Promise.all([
          StudentSummaryService.getStudentSummaryHistory(studentId, 3),
          VideoService.getWatchHistory(studentId, 10),
          ConversationService.getUserRecentConversations(studentId),
          supabase.rpc('get_student_activity_counts', {
            student_ids: [studentId]
          }),
          // Fetch filtered student list for prev/next navigation
          UserService.getStudentsWithActivity(
            studentData.school_id || '',
            {
              grade: filterGrade || undefined,
              class: filterClass || undefined,
              groupId: filterGroupId || undefined
            }
          ),
          PortfolioService.getPortfolio(studentId),
        ])

        // Find current student position and set prev/next
        if (studentList && studentList.length > 0) {
          // Sort same as StudentListTabContent（前後移動が一覧の並びとずれないように同じ関数を使う）
          studentList.sort(compareStudentsForRoster)

          const currentIndex = studentList.findIndex(s => s.id === studentId)
          if (currentIndex > 0) {
            setPrevStudentId(studentList[currentIndex - 1].id)
          } else {
            setPrevStudentId(null)
          }
          if (currentIndex < studentList.length - 1) {
            setNextStudentId(studentList[currentIndex + 1].id)
          } else {
            setNextStudentId(null)
          }
        }

        setSummaries(summaryData)
        setVideoHistory(videoData)
        setChatHistory(chatData)
        setPortfolio(portfolioData)

        // Set activity counts from RPC result
        if (activityCounts.data && activityCounts.data.length > 0) {
          const counts = activityCounts.data[0]
          setVideoCount(Number(counts.watched_videos_count))
          setChatCount(Number(counts.active_conversations_count))
        } else {
          setVideoCount(0)
          setChatCount(0)
        }
      } catch (error) {
        console.error('Failed to load student details:', error)
        setError('生徒情報の読み込みに失敗しました')
      } finally {
        setIsLoading(false)
      }
    }

    loadStudentDetails()
  }, [studentId, navigate, filterGrade, filterClass, filterGroupId])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
        </div>
      </div>
    )
  }

  if (error || !student) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-red-800 mb-2">エラーが発生しました</h2>
          <p className="text-red-700">{error || '生徒情報が見つかりません'}</p>
          <Link
            to={backLink}
            className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
          >
            生徒一覧に戻る
          </Link>
        </div>
      </div>
    )
  }

  const latestSummary = summaries[0] || null

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8">
      {/* Back Link */}
      <div className="mb-6">
        <Link
          to={backLink}
          className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800"
        >
          ← 生徒一覧に戻る
        </Link>
      </div>

      {/* Student Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          {/* Previous Student Navigation */}
          <div className="w-10 flex-shrink-0">
            {prevStudentLink ? (
              <Link
                to={prevStudentLink}
                className="flex items-center justify-center w-10 h-10 text-gray-400 hover:text-purple-600 transition-colors"
                title="前の生徒"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </Link>
            ) : (
              <div className="w-10 h-10 flex items-center justify-center text-gray-200">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </div>
            )}
          </div>

          {/* Student Info */}
          <div className="flex-1 flex items-center justify-between px-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{student.name}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {student.grade} {student.class}
                {student.attendance_number && ` / 出席番号: ${student.attendance_number}`}
              </p>
            </div>
            <table className="border-separate" style={{ borderSpacing: '2rem 0' }}>
              <thead>
                <tr>
                  <th className="text-xs font-normal text-gray-500 pb-1">状況</th>
                  <th className="text-xs font-normal text-gray-500 pb-1">動画数</th>
                  <th className="text-xs font-normal text-gray-500 pb-1">ワーク数</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center">
                    <div className="text-3xl" style={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif' }}>
                      {latestSummary?.weather_icon || '－'}
                    </div>
                  </td>
                  <td className="text-center text-2xl font-bold text-gray-900">
                    {videoCount}
                  </td>
                  <td className="text-center text-2xl font-bold text-gray-900">
                    {chatCount}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Next Student Navigation */}
          <div className="w-10 flex-shrink-0">
            {nextStudentLink ? (
              <Link
                to={nextStudentLink}
                className="flex items-center justify-center w-10 h-10 text-gray-400 hover:text-purple-600 transition-colors"
                title="次の生徒"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
                </svg>
              </Link>
            ) : (
              <div className="w-10 h-10 flex items-center justify-center text-gray-200">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Portfolio */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">キャリアサマリ</h2>
        {portfolio ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <tbody>
                {PORTFOLIO_FIELDS.map(({ key, label }, idx) => (
                  <tr key={key} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="w-40 px-4 py-3 text-sm font-medium text-gray-700 border-r border-gray-200 align-top whitespace-nowrap">
                      {label}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {portfolio[key] ? (
                        <span className="whitespace-pre-wrap">{portfolio[key]}</span>
                      ) : (
                        <span className="text-gray-400">未入力</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">まだキャリアサマリが作成されていません</p>
        )}
      </div>

      {/* Current Summary Details */}
      {latestSummary && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            最新の状況 ({formatDate(latestSummary.summary_date)})
          </h2>

          <div className="space-y-4">
            {/* Student State */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-bold text-gray-800 mb-2">生徒の状況</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                {latestSummary.student_state}
              </p>
            </div>

            {/* Strengths */}
            {latestSummary.strengths.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-bold text-gray-800 mb-2">良い点</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {latestSummary.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Challenges */}
            {latestSummary.challenges.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-bold text-gray-800 mb-2">課題</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {latestSummary.challenges.map((challenge, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>{challenge}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Support Strategy */}
            {latestSummary.teacher_support_strategy.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-bold text-gray-800 mb-2">支援方針</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {latestSummary.teacher_support_strategy.map((strategy, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>{strategy}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Analysis Metadata */}
            <div className="border-t border-gray-200 pt-4 text-xs text-gray-500">
              <p>分析対象: {latestSummary.conversations_analyzed}件の会話</p>
            </div>
          </div>
        </div>
      )}

      {/* Video History */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">閲覧した動画一覧</h2>
        {videoHistory.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">まだ動画を視聴していません</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    月日
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    動画名
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {videoHistory.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(item.watched_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <Link
                        to={`/video/${item.video_id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {item.video?.title || '(タイトルなし)'}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Chat History */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">実施したワーク一覧</h2>
        {chatHistory.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">まだワークを実施していません</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    月日
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    ワーク名
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {chatHistory.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {formatDate(item.last_message_at || item.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {item.chat_prompt?.video_id ? (
                        <Link
                          to={`/video/${item.chat_prompt.video_id}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {item.chat_prompt?.title || item.title || '(タイトルなし)'}
                        </Link>
                      ) : (
                        item.chat_prompt?.title || item.title || '(タイトルなし)'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Back Link - Bottom */}
      <div className="mt-6">
        <Link
          to={backLink}
          className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800"
        >
          ← 生徒一覧に戻る
        </Link>
      </div>
    </div>
  )
}

export default TeacherStudentDetailPage
